import { Period, toDefaultLoadPeriod } from './util/dates';
import { useCallback, useEffect, useRef, useState } from 'react';

import { EmployeeMap } from './api/model';
import { useDelayedInterval } from './hooks/useDelayedInterval';

/**
 * Effect-only component that checks for new messages and shift updates.
 */
export default function IntervalPolling({
  currentDate,
  callGetMessages,
  checkUnreadMessageCount,
  teamId,
  employees,
  loadShifts,
  unreadMessagesPollingSeconds,
  teamRosterPollingSeconds,
  messagesPollingSeconds,
  activeScope,
}: {
  currentDate: Date | null;
  callGetMessages: (
    personIds: number[],
    from: Date,
    to: Date,
    isPoll: boolean
  ) => void;
  checkUnreadMessageCount: (
    teamIds: string[],
    from: Date,
    to: Date,
    isPoll: boolean
  ) => void;
  teamId: string;
  employees: Record<string, EmployeeMap>;
  loadShifts: (teamId: string, requestPeriod: Period, isPoll: boolean) => void;
  unreadMessagesPollingSeconds: number;
  teamRosterPollingSeconds: number;
  messagesPollingSeconds: number;
  activeScope: number;
}) {
  // Doing a fair bit of memoization here to prevent unnecessary calls to getMessages.
  const [personIds, setPersonIds] = useState<number[]>([]);
  const getMessagesRef = useRef(callGetMessages);
  getMessagesRef.current = callGetMessages;
  const checkUnreadMessageCountRef = useRef(checkUnreadMessageCount);
  checkUnreadMessageCountRef.current = checkUnreadMessageCount;
  const loadShiftsRef = useRef(loadShifts);
  loadShiftsRef.current = loadShifts;

  useEffect(() => {
    const employeesForTeam = employees[teamId];
    if (employeesForTeam) {
      setPersonIds((personIds) => {
        const newPersonIds = Object.keys(employeesForTeam).map(Number);
        if (
          personIds.length === newPersonIds.length &&
          personIds.every((id) => employeesForTeam[id.toString()])
        ) {
          return personIds;
        }
        return newPersonIds;
      });
    }
  }, [employees, teamId]);

  // Periodically check for new messages for the selected team. Selecting a new team automatically starts checking messages for that team instead.
  usePoll(
    useCallback(
      (isPoll) => {
        if (currentDate && personIds.length) {
          const period = toDefaultLoadPeriod(currentDate, activeScope);
          getMessagesRef.current(personIds, period.from, period.to, isPoll);
        }
      },
      [currentDate, personIds]
    ),
    messagesPollingSeconds
  );

  usePoll(
    useCallback(
      (isPoll) => {
        if (teamId && currentDate) {
          const period = toDefaultLoadPeriod(currentDate, activeScope);
          checkUnreadMessageCountRef.current(
            [teamId],
            period.from,
            period.to,
            isPoll
          );
        }
      },
      [currentDate, teamId]
    ),
    unreadMessagesPollingSeconds,
    50
  );

  // Periodically check for new shifts for the selected team. Selecting a new team automatically starts checking shifts for that team instead.
  usePoll(
    useCallback(
      (isPoll) => {
        // TeamId is an empty string after login, and until a team is selected.
        if (teamId && currentDate) {
          loadShiftsRef.current(
            teamId,
            toDefaultLoadPeriod(currentDate, activeScope),
            isPoll
          );
        }
      },
      [currentDate, teamId]
    ),
    teamRosterPollingSeconds,
    100
  );

  return null;
}

/**
 * @param poller Should be properly memoized with useCallback.
 */
function usePoll(
  poller: (isPoll: boolean) => void,
  intervalInSeconds = 30,
  delayInMs = 0
) {
  const intervalMs = window.pdc.pollingIntervalMs ?? intervalInSeconds * 1000;
  if (intervalMs === 0) return;

  // useEffect + useInterval since useInterval is not called immediately.
  useEffect(() => poller(false), [poller]);

  useDelayedInterval(() => poller(true), intervalMs, delayInMs);
}
